import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { getRentalCalendar } from "../lib/api";

const iso = (d) => d.toISOString().slice(0, 10);
const DAYS = ["L", "M", "M", "J", "V", "S", "D"];
const DAY_KEYS = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"];

function monthGrid(year, month, byDate) {
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (first.getUTCDay() + 6) % 7;
  const count = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells = Array.from({ length: offset }, () => null);
  for (let i = 1; i <= count; i++) {
    const key = iso(new Date(Date.UTC(year, month, i)));
    cells.push({ day: i, key, info: byDate[key] });
  }
  return cells;
}

export default function RentalCalendarDialog({ listing, onClose }) {
  const [days, setDays] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 90);
    getRentalCalendar(listing.id, iso(start), iso(end))
      .then((r) => setDays(r.days || []))
      .catch(() => setError(true));
  }, [listing.id]);

  const byDate = Object.fromEntries((days || []).map((d) => [d.date, d]));
  const today = new Date();
  const months = [0, 1, 2].map((k) => new Date(today.getFullYear(), today.getMonth() + k, 1));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rental-calendar-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#0b1f33]">{listing.title}</DialogTitle>
          <DialogDescription>Disponibilités des 90 prochains jours · mises à jour en temps réel</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-red-600" data-testid="rental-calendar-error">Impossible de charger les disponibilités pour le moment.</p>}
        {!days && !error && <p className="text-sm text-slate-400 py-10 text-center">Chargement…</p>}
        {days && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {months.map((m) => (
              <div key={m.getMonth()}>
                <p className="text-sm font-600 text-[#0b1f33] capitalize mb-2">{m.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</p>
                <div className="grid grid-cols-7 gap-1 text-[11px] text-slate-400 mb-1">{DAYS.map((d, i) => <span key={DAY_KEYS[i]} className="text-center">{d}</span>)}</div>
                <div className="grid grid-cols-7 gap-1">
                  {monthGrid(m.getFullYear(), m.getMonth(), byDate).map((c, i) => {
                    if (!c) return <span key={`pad-${m.getMonth()}-${i}`} />;
                    const cls = !c.info ? "bg-slate-50 text-slate-300" : c.info.available ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700 line-through";
                    return <span key={c.key} title={c.info?.price ? `${c.info.price} / nuit` : c.key} className={`h-8 rounded flex items-center justify-center text-xs ${cls}`}>{c.day}</span>;
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-slate-100 mt-2">
          <div className="flex gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded bg-emerald-100 inline-block" /> Disponible</span>
            <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded bg-rose-100 inline-block" /> Réservé</span>
          </div>
          <Link to="/contact" className="btn-gold px-6 py-2.5 rounded-sm text-sm font-600" data-testid="rental-booking-cta">Demander une réservation</Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
